/*
 * Head office (draft 11), for owners at HQ or a brand: every company below as brands with their locations folding open
 * (a page at a time), each location's service area (cities, about how many miles, ZIPs) and where a record goes, one team
 * list across brands, and each franchise's plan and bills from The Trades.
 * Only totals and counts, never a client's name.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ADDON_WORDS,
  cleanZips,
  oneLine,
  type AddonRow,
  type HqPersonRow,
  type LocationLine,
  type OverviewAnswer,
  type OverviewRow,
  type PlanAnswer,
  type Role,
  type TerritoryRow,
} from "@steward/shared";
import {
  useChargeNow,
  useEndRole,
  useFranchises,
  useGiveRole,
  useHqPeople,
  useLoginOff,
  useOverview,
  usePlace,
  useSavePlan,
  useSetTerritory,
  useTerritories,
} from "../api/hq";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Icon } from "../components/Icon";
import { Pager } from "./ClientCards";
import { phoneWords, SubLocationSheet } from "../components/SubLocations";
import { Choice, Combobox, InputText } from "../components/fields";
import { monthName, money, plural, shortDate, todayIn } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import {
  ago,
  Banner,
  Button,
  Card,
  Details,
  EmptyState,
  LoadError,
  Page,
  ROLE_WORDS,
  SettingRow,
  Skeleton,
  StatusLabel,
  Switch,
  type Status,
} from "../components/parts";

type Tab = "companies" | "territories" | "people" | "franchises";

const LEVEL_WORDS = { hq: "Head office", brand: "Brand", location: "Location" } as const;
export const TWO_STEP_WORDS: Record<string, [string, Status]> = {
  app: ["Two-step on", "success"],
  not_set_up: ["Two-step not set up", "critical"],
  not_asked: ["No two-step needed", "inactive"],
};
const BILL_WORDS: Record<string, [string, Status]> = {
  paid: ["Paid", "success"],
  open: ["Not charged yet", "informative"],
  processing: ["Bank payment on its way", "informative"],
  failed: ["Card turned down", "critical"],
};
const roleName = (r: Role) => ROLE_WORDS[r] ?? r;
const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

export function HeadOffice() {
  const { company } = useSession();
  const [tab, setTab] = useState<Tab>("companies");
  if (company.level === "location")
    return (
      <Page title="Head office">
        <EmptyState icon="building" title="Head office looks at the companies below">
          Switch to the brand or to The Trades to see every location.
        </EmptyState>
      </Page>
    );
  return (
    <Page title="Head office" subtitle={company.name}>
      <div className="segmented page-tabs" role="tablist" aria-label="Head office">
        {(
          [
            ["companies", "Companies"],
            ["territories", "Territories"],
            ["people", "Team"],
            ["franchises", "Franchises"],
          ] as const
        ).map(([v, label]) => (
          <button key={v} type="button" role="tab" aria-selected={tab === v} onClick={() => setTab(v)}>
            {label}
          </button>
        ))}
      </div>
      {tab === "companies" ? (
        <Companies />
      ) : tab === "territories" ? (
        <Territories />
      ) : tab === "people" ? (
        <People />
      ) : (
        <Franchises />
      )}
    </Page>
  );
}

// Companies -------------------------------------------------------------------------------------------------------------

/** The review and failed bill banners and the totals tiles: the Companies tab, and Home at The Trades. */
export function OverviewTiles({ totals: t }: { totals: OverviewAnswer["totals"] }) {
  return (
    <>
      {t.reviewOverdue ? (
        <Banner
          type="warning"
          action={
            <Link to="/security" className="btn secondary small">
              Start the review
            </Link>
          }
        >
          The access review is due. Look over every login once a quarter.
        </Banner>
      ) : null}
      {t.billsFailed ? (
        <Banner type="error">
          {t.billsFailed === 1 ? "One franchise card was" : `${String(t.billsFailed)} franchise cards were`} turned down. Their
          work goes on. See Franchises.
        </Banner>
      ) : null}
      <div className="tiles">
        <Tile label="Collected" value={money(t.collected30Cents)} hint="The last 30 days, every company" />
        <Tile label="Customers served" value={String(t.customersServed)} hint={`${String(t.newThisMonth)} new this month`} />
        <Tile
          label="Repeating money"
          value={money(t.repeatingMonthlyCents)}
          hint={`A month, from ${String(t.repeatingClients)} repeating clients`}
        />
        <Tile
          label="Last 12 months"
          value={money(t.collected12Cents)}
          hint={`About ${money(Math.round(t.collected12Cents / 12))} a month`}
        />
      </div>
    </>
  );
}

/** Rows a page in the company list: brands, and whatever has been folded open under them. */
const COMPANY_PAGE = 25;
type Money = "collected30Cents" | "collected12Cents" | "customersServed" | "repeatingMonthlyCents";
type Sub = OverviewRow["subLocations"][number];
/** One row of the tree: a company (brand or location) with its numbers added up from everything under it, or a sub location. */
type CompanyLine =
  | { kind: "company"; key: string; depth: number; row: OverviewRow; kids: number; sums: Record<Money, number> }
  | { kind: "sub"; key: string; depth: number; sub: Sub; index: number; parent: OverviewRow }
  | { kind: "add"; key: string; depth: number; parent: OverviewRow };

/**
 * Every company below as a tree, most money first: a brand with its numbers added up from its locations, folding open to
 * its locations, and a location folding open to its sub locations (real branches with their own name, line, ZIPs, email
 * and address, like The Cleaners of Metairie under The Cleaners of New Orleans), then a row to add one. Tap a sub location
 * to change it. 25 rows a page with back and next, so thousands of locations never make one endless list. Searching
 * lists every company and sub location that matches.
 */
function Companies() {
  const { company } = useSession();
  const q = useOverview(company.id);
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const [text, setText] = useState("");
  const [at, setAt] = useState(0);
  const [editing, setEditing] = useState<{ parent: OverviewRow; index: number } | null>(null);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const o = q.data;
  if (!o) return <Skeleton rows={6} />;
  const keys: Money[] = ["collected30Cents", "collected12Cents", "customersServed", "repeatingMonthlyCents"];
  const childrenOf = (id: string) => o.rows.filter((r) => r.parentId === id);
  /** The brand a company sits in, for its sub locations' names. */
  const brandOf = (r: OverviewRow): string => {
    const up = o.rows.find((x) => x.companyId === r.parentId);
    return r.level === "brand" || !up ? r.name : brandOf(up);
  };
  const under = (id: string): OverviewRow[] => childrenOf(id).flatMap((r) => [r, ...under(r.companyId)]);
  const line = (row: OverviewRow, depth: number): CompanyLine => {
    const all = [row, ...under(row.companyId)];
    const sums = Object.fromEntries(keys.map((k) => [k, all.reduce((a, x) => a + x[k], 0)])) as Record<Money, number>;
    // A location always folds open, to its sub locations and the row that adds one.
    const kids = childrenOf(row.companyId).length + row.subLocations.length + (row.level === "location" ? 1 : 0);
    return { kind: "company", key: row.companyId, depth, row, kids, sums };
  };
  const subLine = (parent: OverviewRow, sub: Sub, index: number, depth: number): CompanyLine => ({
    kind: "sub",
    key: `${parent.companyId}:${sub.name}`,
    depth,
    sub,
    index,
    parent,
  });
  const most = (a: CompanyLine, b: CompanyLine) =>
    a.kind === "company" && b.kind === "company"
      ? b.sums.collected30Cents - a.sums.collected30Cents || a.row.name.localeCompare(b.row.name)
      : 0;
  /** A company's line and, when folded open, its sub locations then its companies, each a step in. */
  const walk = (row: OverviewRow, depth: number): CompanyLine[] => {
    const me = line(row, depth);
    if (!opened.has(row.companyId)) return [me];
    return [
      me,
      ...row.subLocations.map((s, i) => subLine(row, s, i, depth + 1)),
      ...(row.level === "location" ? [{ kind: "add" as const, key: `${row.companyId}:add`, depth: depth + 1, parent: row }] : []),
      ...childrenOf(row.companyId)
        .map((r) => line(r, depth + 1))
        .sort(most)
        .flatMap((l) => (l.kind === "company" ? walk(l.row, depth + 1) : [])),
    ];
  };
  const term = text.trim().toLowerCase();
  const lines: CompanyLine[] = term
    ? [
        ...o.rows.filter((r) => `${r.name} ${r.ownerName ?? ""}`.toLowerCase().includes(term)).map((r) => line(r, 0)),
        ...o.rows.flatMap((r) =>
          r.subLocations.flatMap((s, i) => (s.fullName.toLowerCase().includes(term) ? [subLine(r, s, i, 0)] : [])),
        ),
      ].sort(most)
    : childrenOf(company.id)
        .map((r) => line(r, 0))
        .sort(most)
        .flatMap((l) => (l.kind === "company" ? walk(l.row, 0) : []));
  const start = at < lines.length ? at : 0;
  const toggle = (id: string) => {
    const next = new Set(opened);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpened(next);
  };
  /** A company's added up number; a sub location's money is in its location's row. */
  const num = (l: CompanyLine, k: Money) =>
    l.kind === "company" ? <span className="num">{k === "customersServed" ? String(l.sums[k]) : money(l.sums[k])}</span> : null;
  return (
    <>
      <OverviewTiles totals={o.totals} />
      <DataTable
        title="The Trades"
        rows={lines.slice(start, start + COMPANY_PAGE)}
        total={lines.length}
        rowKey={(l) => l.key}
        query={{
          value: text,
          onChange: (v) => {
            setText(v);
            setAt(0);
          },
        }}
        onRowClick={(l) =>
          l.kind === "company"
            ? l.kids && !term
              ? toggle(l.row.companyId)
              : undefined
            : setEditing({ parent: l.parent, index: l.kind === "sub" ? l.index : -1 })
        }
        rowClass={(l) => (l.kind === "company" ? undefined : "tree-sub")}
        empty="Add a brand or a location in Settings, Business info."
        footer={<Pager at={start} total={lines.length} go={setAt} size={COMPANY_PAGE} labels={["Back a page", "Next page"]} />}
        columns={[
          {
            key: "name",
            header: "Company",
            render: (l) => (
              <div className="tree-name" style={{ paddingLeft: l.depth * 26 }}>
                {l.kind === "company" && l.kids && !term ? (
                  <Icon name={opened.has(l.row.companyId) ? "chevronDown" : "chevronRight"} size={18} />
                ) : (
                  <span className="tree-gap" aria-hidden="true" />
                )}
                {l.kind === "company" ? (
                  <div className="name-cell">
                    <b>{l.row.name}</b>
                    <span className="muted small">
                      {LEVEL_WORDS[l.row.level]}
                      {treeCount(l.row, childrenOf(l.row.companyId).length).replace(/^(?=.)/, ", ")}
                      {l.row.status === "paused" ? ". Paused" : ""}
                    </span>
                  </div>
                ) : l.kind === "sub" ? (
                  <div className="name-cell">
                    <b>{l.sub.fullName}</b>
                    <span className="muted small">
                      {[
                        `Sub location${term ? ` of ${l.parent.name}` : ""}`,
                        plural(l.sub.zips.length, "ZIP"),
                        phoneWords(l.sub.phone),
                        l.sub.email ?? "",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                ) : (
                  <span className="tree-add">
                    <Icon name="plus" size={16} /> Add a sub location
                  </span>
                )}
              </div>
            ),
          },
          {
            key: "run",
            header: "Run by",
            render: (l) =>
              l.kind === "company" ? (
                runBy(l.row)
              ) : l.kind === "sub" ? (
                <span className="muted">{l.sub.address ? oneLine(l.sub.address) : "No address yet"}</span>
              ) : null,
          },
          { key: "c30", header: "Last 30 days", align: "right", render: (l) => num(l, "collected30Cents") },
          { key: "c12", header: "Last 12 months", align: "right", render: (l) => num(l, "collected12Cents") },
          { key: "served", header: "Customers", align: "right", render: (l) => num(l, "customersServed") },
          { key: "rep", header: "Repeating a month", align: "right", render: (l) => num(l, "repeatingMonthlyCents") },
          {
            key: "bill",
            header: "Fee",
            render: (l) =>
              l.kind === "company" && l.row.billStatus ? (
                <StatusLabel status={BILL_WORDS[l.row.billStatus]![1]}>{BILL_WORDS[l.row.billStatus]![0]}</StatusLabel>
              ) : null,
          },
        ]}
      />
      {editing ? (
        <SubLocationSheet
          location={{ id: editing.parent.companyId, name: editing.parent.name }}
          brand={brandOf(editing.parent)}
          subs={editing.parent.subLocations.map(lineOf)}
          index={editing.index}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <Card title="Collected by month">
        <DataTable
          title="Months"
          rows={o.months}
          rowKey={(m) => m.month}
          columns={[
            { key: "m", header: "Month", render: (m) => monthName(`${m.month}-01`) },
            {
              key: "c",
              header: "Collected",
              align: "right",
              render: (m) => <span className="num">{money(m.collectedCents)}</span>,
            },
          ]}
        />
      </Card>
    </>
  );
}

/** An overview row's sub location in the shape it is saved in. */
const lineOf = (x: OverviewRow["subLocations"][number]): LocationLine => ({
  name: x.name,
  phone: x.phone,
  zips: x.zips,
  ...(x.email ? { email: x.email } : {}),
  ...(x.address ? { address: x.address } : {}),
});

/** "2 locations", "1 sub location", or both, for what folds open under a company. */
function treeCount(r: OverviewRow, companies: number): string {
  return [
    companies ? plural(companies, "location") : "",
    r.subLocations.length ? plural(r.subLocations.length, "sub location") : "",
  ]
    .filter(Boolean)
    .join(" and ");
}

function runBy(r: OverviewRow) {
  if (r.level !== "location") return <span className="muted">Head office</span>;
  return (
    <div className="name-cell">
      <span>{r.runBy === "franchise" ? (r.ownerName ?? "Franchise owner") : "Head office"}</span>
      {r.namesPrivate ? <span className="muted small">Customer names private</span> : null}
    </div>
  );
}

// Territories ----------------------------------------------------------------------------------------------------------

/** "New Orleans, Metairie, Kenner and 4 more" for a location's cities. */
function cityWords(areas: TerritoryRow["areas"]): string {
  const names = areas.filter((a) => a.city !== "Other").map((a) => a.city);
  if (!names.length) return areas.length ? "ZIPs not on our map" : "";
  return `${names.slice(0, 4).join(", ")}${names.length > 4 ? ` and ${String(names.length - 4)} more` : ""}`;
}

/**
 * Where each location works: the cities its ZIP codes are in and about how many miles out it goes. Brands hold no ZIPs
 * (their locations do), so only locations show. Tap one to see every ZIP by city and change them.
 */
function Territories() {
  const { company } = useSession();
  const q = useTerritories(company.id);
  const [open, setOpen] = useState<TerritoryRow | null>(null);
  const all = q.data?.rows ?? [];
  const names = new Map(all.map((r) => [r.companyId, r.name]));
  const rows = all.filter((r) => r.level === "location");
  return (
    <>
      <PlaceCheck />
      <DataTable
        title="Service areas"
        rows={rows}
        rowKey={(r) => r.companyId}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} retry={() => void q.refetch()} /> : undefined}
        onRowClick={setOpen}
        search={(r) => `${r.name} ${r.areas.map((a) => a.city).join(" ")} ${r.zips.join(" ")}`}
        empty="No locations yet. Add one in Settings, Business info."
        columns={[
          {
            key: "name",
            header: "Location",
            sort: (r) => r.name,
            render: (r) => (
              <div className="name-cell">
                <b>{r.name}</b>
                <span className="muted small">
                  {r.parentId && r.parentId !== company.id ? (names.get(r.parentId) ?? "") : "Location"}
                  {r.franchise ? ". Franchise" : ""}
                </span>
              </div>
            ),
          },
          {
            key: "serves",
            header: "Serves",
            render: (r) => (r.zips.length ? <span>{cityWords(r.areas)}</span> : <span className="muted">None yet</span>),
          },
          {
            key: "radius",
            header: "Reach",
            align: "right",
            sort: (r) => r.radiusMiles ?? 0,
            render: (r) =>
              r.radiusMiles ? <span className="num">About {r.radiusMiles} miles</span> : <span className="muted">None yet</span>,
          },
          {
            key: "n",
            header: "ZIP codes",
            align: "right",
            sort: (r) => r.zips.length,
            render: (r) => <span className="num">{String(r.zips.length)}</span>,
          },
        ]}
      />
      {open ? <ZipEditor row={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function ZipEditor({ row, onClose }: { row: TerritoryRow; onClose: () => void }) {
  const [text, setText] = useState(row.zips.join(", "));
  const [err, setErr] = useState<string | null>(null);
  const save = useSetTerritory();
  const toast = useToast();
  const { zips, bad } = cleanZips(text);
  return (
    <Modal
      title={`ZIP codes for ${row.name}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() => {
              if (bad.length) {
                setErr(`Use 5 digits for each ZIP. Check ${bad.slice(0, 3).join(", ")}.`);
                return;
              }
              save.mutate(
                { companyId: row.companyId, zips },
                {
                  onSuccess: () => {
                    toast(`Saved ${String(zips.length)} ZIP codes`);
                    onClose();
                  },
                  onError: (e) => setErr(errText(e)),
                },
              );
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <p className="muted">A ZIP can have only one location in a brand. Requests from these ZIPs go to {row.name}.</p>
      {row.radiusMiles ? <p className="muted small">Reaches about {row.radiusMiles} miles from the middle of its ZIPs.</p> : null}
      {row.areas.length ? (
        <ul className="zip-areas">
          {row.areas.map((a) => (
            <li key={`${a.city}-${a.state}`}>
              <b>
                {a.city}
                {a.state ? `, ${a.state}` : ""}
              </b>
              <span className="muted small num">{a.zips.join(", ")}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <label className="field">
        <span className="label">ZIP codes</span>
        <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} aria-describedby="zip-help" />
        <span id="zip-help" className="muted small">
          Type or paste them with spaces or commas. {String(zips.length)} ZIP codes.
        </span>
      </label>
      {err ? <Banner type="error">{err}</Banner> : null}
    </Modal>
  );
}

function PlaceCheck() {
  const [text, setText] = useState("");
  const place = usePlace();
  const r = place.data;
  return (
    <Card title="Where does a request go">
      <div className="row wrap">
        <InputText
          label="ZIP or address"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="70115 or 12 Oak St, New Orleans, LA 70115"
          wide
        />
        <Button
          type="secondary"
          loading={place.isPending}
          onClick={() => place.mutate(/^\d{5}$/.test(text.trim()) ? { zip: text.trim() } : { address: text })}
        >
          Check
        </Button>
      </div>
      {r ? (
        <Banner type={r.outside ? "warning" : "success"}>
          {r.how === "zip"
            ? `It goes to ${r.name}. That ZIP is in its territory.`
            : `No location lists that ZIP, so it goes to ${r.name}, the home location.`}
        </Banner>
      ) : null}
      {place.error ? <Banner type="error">{errText(place.error)}</Banner> : null}
    </Card>
  );
}

// People ---------------------------------------------------------------------------------------------------------------

function People() {
  const { company } = useSession();
  const q = useHqPeople(company.id);
  const [open, setOpen] = useState<HqPersonRow | null>(null);
  const current = open ? (q.data?.find((p) => p.userId === open.userId) ?? open) : null;
  return (
    <>
      <DataTable
        title="Your team"
        rows={q.data ?? []}
        rowKey={(p) => p.userId}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} retry={() => void q.refetch()} /> : undefined}
        onRowClick={setOpen}
        search={(p) => `${p.name} ${p.email ?? ""} ${p.memberships.map((m) => m.companyName).join(" ")}`}
        empty="People show here once they are invited to a company."
        columns={[
          {
            key: "name",
            header: "Name",
            sort: (p) => p.name,
            render: (p) => (
              <div className="name-cell">
                <b>{p.name}</b>
                <span className="muted small">{p.email ?? p.phone ?? ""}</span>
              </div>
            ),
          },
          {
            key: "roles",
            header: "Works at",
            render: (p) => {
              const live = p.memberships.filter((m) => m.active);
              return live.length ? (
                <span>{live.map((m) => `${roleName(m.role)} at ${m.companyName}`).join(". ")}</span>
              ) : (
                <span className="muted">No access now</span>
              );
            },
          },
          {
            key: "two",
            header: "Two-step",
            render: (p) =>
              p.disabled ? (
                <StatusLabel status="inactive">Turned off</StatusLabel>
              ) : (
                <StatusLabel status={TWO_STEP_WORDS[p.twoStep]![1]}>{TWO_STEP_WORDS[p.twoStep]![0]}</StatusLabel>
              ),
          },
          {
            key: "last",
            header: "Last active",
            sort: (p) => p.lastActiveAt ?? "",
            render: (p) => (p.lastActiveAt ? ago(p.lastActiveAt) : <span className="muted">Never</span>),
          },
        ]}
      />
      {current ? <PersonSheet person={current} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

const GIVE_ROLES: Role[] = ["manager", "dispatch", "crew", "franchise_owner", "owner"];

function PersonSheet({ person, onClose }: { person: HqPersonRow; onClose: () => void }) {
  const { company, me } = useSession();
  const places = useTerritories(company.id).data?.rows ?? [];
  const give = useGiveRole();
  const end = useEndRole();
  const off = useLoginOff();
  const toast = useToast();
  const [where, setWhere] = useState("");
  const [role, setRole] = useState<Role>("crew");
  const [confirmOff, setConfirmOff] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const self = person.userId === me.id;
  const done = (words: string) => ({ onSuccess: () => toast(words), onError: (e: unknown) => setErr(errText(e)) });
  return (
    <Modal title={person.name} open onClose={onClose}>
      <Details
        rows={[
          ["Email", person.email ?? "None"],
          ["Phone", person.phone ?? "None"],
          ["Two-step", person.disabled ? "Turned off" : TWO_STEP_WORDS[person.twoStep]![0]],
          ["Last active", person.lastActiveAt ? ago(person.lastActiveAt) : "Never"],
        ]}
      />
      <Card title="Roles">
        {person.memberships.map((m) => (
          <SettingRow
            key={m.companyId}
            title={m.companyName}
            description={m.active ? roleName(m.role) : `Was ${roleName(m.role)}. No access now`}
            control={
              m.active && !self ? (
                <Button
                  type="tertiary"
                  size="small"
                  variation="destructive"
                  loading={end.isPending}
                  onClick={() => end.mutate({ userId: person.userId, companyId: m.companyId }, done("Role taken away"))}
                >
                  Take away
                </Button>
              ) : null
            }
          />
        ))}
      </Card>
      {!self && !person.disabled ? (
        <Card title="Give a role">
          <Combobox
            label="Company"
            value={where}
            onChange={setWhere}
            options={places.filter((p) => p.level !== "hq").map((p) => ({ value: p.companyId, label: p.name }))}
          />
          <Choice
            label="Role"
            value={role}
            onChange={setRole}
            options={GIVE_ROLES.map((r) => ({ value: r, label: roleName(r) }))}
          />
          <Button
            type="secondary"
            loading={give.isPending}
            onClick={() => {
              if (!where) {
                setErr("Pick a company first.");
                return;
              }
              give.mutate({ userId: person.userId, companyId: where, role }, done("Role saved"));
            }}
          >
            Save role
          </Button>
        </Card>
      ) : null}
      {err ? <Banner type="error">{err}</Banner> : null}
      {self ? null : person.disabled ? (
        <Button type="secondary" onClick={() => off.mutate({ userId: person.userId, off: false }, done("Login turned back on"))}>
          Turn login back on
        </Button>
      ) : confirmOff ? (
        <Banner
          type="warning"
          action={
            <Button
              variation="destructive"
              loading={off.isPending}
              onClick={() => off.mutate({ userId: person.userId, off: true }, done("Login turned off"))}
            >
              Turn off
            </Button>
          }
        >
          {person.name} can't sign in anywhere and every device signs out now. Their records stay for payroll and tax.
        </Banner>
      ) : (
        <Button type="tertiary" variation="destructive" onClick={() => setConfirmOff(true)}>
          Turn login off
        </Button>
      )}
    </Modal>
  );
}

// Franchises -----------------------------------------------------------------------------------------------------------

function Franchises() {
  const { company } = useSession();
  const q = useFranchises(company.id);
  const [open, setOpen] = useState<string | null>(null);
  const current = q.data?.find((p) => p.companyId === open) ?? null;
  return (
    <>
      <p className="muted">
        A franchise pays The Trades the software fee each month, plus any paid add-ons. A location head office runs pays nothing.
        A late bill never locks a location&apos;s work.
      </p>
      <DataTable
        title="Locations"
        rows={q.data ?? []}
        rowKey={(p) => p.companyId}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} retry={() => void q.refetch()} /> : undefined}
        onRowClick={(p) => setOpen(p.companyId)}
        search={(p) => `${p.name} ${p.ownerName ?? ""}`}
        empty="No locations yet."
        columns={[
          {
            key: "name",
            header: "Location",
            sort: (p) => p.name,
            render: (p) => (
              <div className="name-cell">
                <b>{p.name}</b>
                <span className="muted small">
                  {p.status === "active"
                    ? (p.ownerName ?? "No franchise owner yet")
                    : p.status === "ended"
                      ? "Franchise ended"
                      : "Head office runs it"}
                </span>
              </div>
            ),
          },
          {
            key: "fee",
            header: "Each month",
            align: "right",
            render: (p) => (p.status === "active" ? <span className="num">{money(monthly(p))}</span> : null),
          },
          {
            key: "add",
            header: "Add-ons",
            render: (p) =>
              p.status === "active"
                ? p.addons
                    .filter((a) => a.on)
                    .map((a) => ADDON_WORDS[a.addon].name)
                    .join(", ") || <span className="muted">None</span>
                : null,
          },
          {
            key: "card",
            header: "Card",
            render: (p) =>
              p.status !== "active" ? null : p.card ? (
                <span>Ends in {p.card.last4}</span>
              ) : (
                <StatusLabel status="warning">No card yet</StatusLabel>
              ),
          },
          {
            key: "bill",
            header: "Last bill",
            render: (p) => {
              const b = p.bills[0];
              return b && p.status !== "none" ? (
                <StatusLabel status={BILL_WORDS[b.status]![1]}>{BILL_WORDS[b.status]![0]}</StatusLabel>
              ) : null;
            },
          },
        ]}
      />
      {current ? <PlanSheet plan={current} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

const monthly = (p: PlanAnswer) => p.feeCents + p.addons.filter((a) => a.on).reduce((s, a) => s + a.priceCents, 0);

function PlanSheet({ plan, onClose }: { plan: PlanAnswer; onClose: () => void }) {
  const { company } = useSession();
  const save = useSavePlan();
  const charge = useChargeNow();
  const toast = useToast();
  const [fee, setFee] = useState((plan.feeCents / 100).toFixed(2));
  const [err, setErr] = useState<string | null>(null);
  const put = (body: Parameters<typeof save.mutate>[0]["body"], words: string) =>
    save.mutate({ companyId: plan.companyId, body }, { onSuccess: () => toast(words), onError: (e) => setErr(errText(e)) });
  if (plan.status === "none")
    return (
      <Modal title={plan.name} open onClose={onClose}>
        <p>Head office runs this location, so it pays no fee.</p>
        <p className="muted">Make it a franchise when a franchise owner signs. The fee starts at $250 a month.</p>
        <Button
          loading={save.isPending}
          onClick={() => put({ status: "active", startedOn: todayIn(company.timezone) }, "It is a franchise now")}
        >
          Make it a franchise
        </Button>
        {err ? <Banner type="error">{err}</Banner> : null}
      </Modal>
    );
  return (
    <Modal title={plan.name} open onClose={onClose}>
      <Details
        rows={[
          ["Franchise owner", plan.ownerName ?? "Not invited yet"],
          ["Started", plan.startedOn ? shortDate(plan.startedOn) : "Not set"],
          ["Card", plan.card ? `${plan.card.brand} ending in ${plan.card.last4}` : "The franchise owner adds it in Settings"],
          ["Each month", money(monthly(plan))],
        ]}
      />
      <Card title="Software fee">
        <div className="row wrap">
          <InputText label="Fee a month" prefix="$" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />
          <Button
            type="secondary"
            onClick={() => {
              const cents = Math.round(Number(fee) * 100);
              if (!Number.isFinite(cents) || cents < 0) {
                setErr("Type the fee in dollars, like 250.");
                return;
              }
              put({ feeCents: cents }, "Fee saved");
            }}
          >
            Save fee
          </Button>
        </div>
      </Card>
      <Card title="Paid add-ons">
        {plan.addons.map((a) => (
          <AddonLine
            key={a.addon}
            a={a}
            onChange={(on) => put({ addons: [{ addon: a.addon, on }] }, on ? "Add-on on" : "Add-on off")}
          />
        ))}
      </Card>
      <SettingRow
        title="Head office sees customer names"
        description="Off means owners above see this franchise's totals, not its clients."
        control={
          <Switch
            label="Head office sees customer names"
            checked={plan.hqSeesNames}
            onChange={(v) => put({ hqSeesNames: v }, v ? "Head office sees names" : "Names kept private")}
          />
        }
      />
      <Card
        title="Bills"
        action={
          <Button
            type="secondary"
            size="small"
            loading={charge.isPending}
            onClick={() =>
              charge.mutate(plan.companyId, { onSuccess: () => toast("Card tried"), onError: (e) => setErr(errText(e)) })
            }
          >
            Try the card now
          </Button>
        }
      >
        <BillTable plan={plan} />
      </Card>
      {err ? <Banner type="error">{err}</Banner> : null}
      {plan.status === "active" ? (
        <Button type="tertiary" variation="destructive" onClick={() => put({ status: "ended" }, "Franchise ended")}>
          End the franchise
        </Button>
      ) : (
        <Button type="secondary" onClick={() => put({ status: "active" }, "Franchise started again")}>
          Start the franchise again
        </Button>
      )}
    </Modal>
  );
}

export function AddonLine({ a, onChange }: { a: AddonRow; onChange?: (on: boolean) => void }) {
  const w = ADDON_WORDS[a.addon];
  const past = Math.max(0, a.usedThisMonth - a.allowance);
  return (
    <SettingRow
      title={`${w.name}, ${money(a.priceCents)} a month`}
      description={`${String(a.allowance)} ${w.unit} a month, then ${money(a.extraCents)} each. This month ${String(a.usedThisMonth)} used${past ? `, ${String(past)} past the allowance` : ""}.`}
      control={
        onChange ? (
          <Switch label={w.name} checked={a.on} onChange={onChange} />
        ) : (
          <StatusLabel status={a.on ? "success" : "inactive"}>{a.on ? "On" : "Off"}</StatusLabel>
        )
      }
    />
  );
}

export function BillTable({ plan }: { plan: PlanAnswer }) {
  return (
    <DataTable
      title="Bills"
      rows={plan.bills}
      rowKey={(b) => b.id}
      empty="The first bill comes on the 1st of next month."
      columns={[
        { key: "m", header: "Month", render: (b) => monthName(`${b.month}-01`) },
        { key: "t", header: "Total", align: "right", render: (b) => <span className="num">{money(b.totalCents)}</span> },
        {
          key: "s",
          header: "",
          render: (b) => (
            <div className="name-cell">
              <StatusLabel status={BILL_WORDS[b.status]![1]}>{BILL_WORDS[b.status]![0]}</StatusLabel>
              {b.failure ? <span className="muted small">{b.failure}</span> : null}
            </div>
          ),
        },
      ]}
    />
  );
}
